import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { LoggedUser } from '../db/db';
import {
  ZendeskEmbeddedTargets,
  ZendeskMessagingCustomization,
  ZendeskMessagingCoordinator,
  ZendeskMessagingState,
  ZendeskUnreadState,
  mapIokLanguageToZendeskLocale,
  zendeskMessagingCoordinator
} from './zendeskMessaging';
import { ChatUnreadSource } from './chatUnreadTelemetry';
import {
  classifyIokSupportPath,
  getIokSupportEnvironment,
  IokSupportEntryPoint,
  IokSupportIdentityMode,
  IokSupportModule,
  normalizePositiveId,
  normalizeUserRole,
  ZendeskSupportContextManager,
  zendeskSupportContextManager
} from './zendeskSupportContext';

interface ZendeskMessagingContextValue extends ZendeskMessagingState {
  unread: ZendeskUnreadState;
  renderEmbedded(targets: ZendeskEmbeddedTargets): Promise<void>;
  applyCustomization(customization: ZendeskMessagingCustomization): void;
  deactivateEmbedded(): void;
  subscribeToClose(listener: () => void): () => void;
  subscribeToConversationOpened(listener: () => void): () => void;
  reopenEmbedded(): void;
  reportChatOpenedFromUnread(source: ChatUnreadSource): void;
  confirmExistingIdentity(): Promise<void>;
  continueAnonymously(): Promise<void>;
  recordSupportEntryPoint(entryPoint: IokSupportEntryPoint): void;
  updateSupportRoute(pathname: string): void;
  retry(): Promise<void>;
}

interface ZendeskMessagingProviderProps {
  children: ReactNode;
  user?: LoggedUser | null;
  language?: string | null;
  coordinator?: ZendeskMessagingCoordinator;
  supportContextManager?: Pick<
    ZendeskSupportContextManager,
    'apply' | 'clear' | 'updateNavigation'
  >;
  onInvalidIokSession?: () => void | Promise<void>;
}

const ZendeskMessagingContext = createContext<ZendeskMessagingContextValue | undefined>(
  undefined
);

export const ZendeskMessagingProvider = ({
  children,
  user,
  language,
  coordinator = zendeskMessagingCoordinator,
  supportContextManager = zendeskSupportContextManager,
  onInvalidIokSession
}: ZendeskMessagingProviderProps) => {
  const [state, setState] = useState<ZendeskMessagingState>(coordinator.getState());
  const [unread, setUnread] = useState<ZendeskUnreadState>(
    coordinator.getUnreadState()
  );
  const [supportRoute, setSupportRoute] = useState(() =>
    classifyIokSupportPath(typeof window === 'undefined' ? '/' : window.location.pathname)
  );
  const [supportEntry, setSupportEntry] = useState<{
    entryPoint: IokSupportEntryPoint;
    initialModule: IokSupportModule;
  }>();
  const supportRouteRef = useRef(supportRoute);
  const supportIdentityKeyRef = useRef<string>();
  const supportIdentityTransitionRef = useRef({
    awaitingSettlement: false,
    sawUnsettledState: false
  });
  const languageRef = useRef(language);
  const invalidIokSessionRef = useRef(onInvalidIokSession);
  languageRef.current = language;
  invalidIokSessionRef.current = onInvalidIokSession;
  supportRouteRef.current = supportRoute;

  const session = useMemo(() => {
    if (user === undefined) return undefined;
    if (!user?.accessToken) return null;
    return {
      userId: user.id ?? user.userName,
      accessToken: user.accessToken
    };
  }, [user]);

  const handleInvalidIokSession = useCallback(
    () => invalidIokSessionRef.current?.(),
    []
  );

  const initialize = useCallback(
    () => {
      if (session === undefined || coordinator.isEnding()) {
        return Promise.resolve();
      }

      return coordinator.initialize({
        session,
        language: languageRef.current,
        onInvalidIokSession: handleInvalidIokSession
      });
    },
    [coordinator, handleInvalidIokSession, session]
  );

  useEffect(() => coordinator.subscribe(setState), [coordinator]);

  useEffect(
    () => coordinator.subscribeToUnreadState(setUnread),
    [coordinator]
  );

  useEffect(() => {
    void initialize();
  }, [initialize]);

  useEffect(() => {
    if (session === undefined || coordinator.isEnding()) return;
    coordinator.updateLocale(language);
  }, [coordinator, language, session]);

  const updateSupportRoute = useCallback((pathname: string) => {
    const next = classifyIokSupportPath(pathname);
    supportRouteRef.current = next;
    setSupportRoute((current) =>
      current.module === next.module && current.path === next.path ? current : next
    );
    supportContextManager.updateNavigation(next.module, next.path);
    if (next.module === 'chat') {
      setSupportEntry((current) => current ?? {
        entryPoint: 'chat_page',
        initialModule: 'chat'
      });
    }
  }, [supportContextManager]);

  const recordSupportEntryPoint = useCallback((entryPoint: IokSupportEntryPoint) => {
    setSupportEntry((current) => ({
      entryPoint,
      initialModule: current?.initialModule ?? supportRouteRef.current.module
    }));
  }, []);

  useEffect(() => {
    const nextIdentityKey = user?.accessToken
      ? `authenticated:${String(user.id ?? user.userName ?? 'unknown')}:${normalizePositiveId(user.companyId) ?? 'unknown'}`
      : user === null ? 'public' : undefined;
    if (supportIdentityKeyRef.current !== nextIdentityKey) {
      if (supportIdentityKeyRef.current !== undefined) {
        supportContextManager.clear();
        setSupportEntry(undefined);
      }
      supportIdentityTransitionRef.current = {
        awaitingSettlement: true,
        sawUnsettledState: false
      };
    }
    supportIdentityKeyRef.current = nextIdentityKey;
  }, [supportContextManager, user]);

  useEffect(() => {
    let identityMode: IokSupportIdentityMode | undefined;
    if (state.status === 'authenticated') {
      identityMode = 'authenticated';
    } else if (state.status === 'anonymous' && user?.accessToken) {
      identityMode = 'iok_user_zendesk_anonymous';
    } else if (
      user === null &&
      state.status === 'ready' &&
      state.mode === 'floating'
    ) {
      identityMode = 'public_anonymous';
    }

    const identityTransition = supportIdentityTransitionRef.current;
    if (identityTransition.awaitingSettlement) {
      if (!identityMode) {
        identityTransition.sawUnsettledState = true;
      } else if (!identityTransition.sawUnsettledState) {
        supportContextManager.clear();
        return;
      } else {
        identityTransition.awaitingSettlement = false;
      }
    }

    if (!identityMode) {
      supportContextManager.clear();
      return;
    }

    const entry = supportEntry ?? (identityMode === 'public_anonymous'
      ? { entryPoint: 'panel' as const, initialModule: supportRoute.module }
      : undefined);
    if (!entry) return;

    void supportContextManager.apply({
      identityMode,
      userId: normalizePositiveId(user?.id),
      companyId: normalizePositiveId(user?.companyId),
      userRole: normalizeUserRole(user?.userType),
      locale: mapIokLanguageToZendeskLocale(language),
      environment: getIokSupportEnvironment(),
      entryPoint: entry.entryPoint,
      initialModule: entry.initialModule,
      currentModule: supportRoute.module,
      currentPath: supportRoute.path
    });
  }, [language, state.mode, state.status, supportContextManager, supportEntry, supportRoute, user]);

  useEffect(() => () => supportContextManager.clear(), [supportContextManager]);

  const renderEmbedded = useCallback(
    (targets: ZendeskEmbeddedTargets) => coordinator.renderEmbedded(targets),
    [coordinator]
  );

  const applyCustomization = useCallback(
    (customization: ZendeskMessagingCustomization) =>
      coordinator.applyCustomization(customization),
    [coordinator]
  );

  const subscribeToClose = useCallback(
    (listener: () => void) => coordinator.subscribeToClose(listener),
    [coordinator]
  );

  const reopenEmbedded = useCallback(
    () => coordinator.reopenEmbedded(),
    [coordinator]
  );

  const subscribeToConversationOpened = useCallback(
    (listener: () => void) => coordinator.subscribeToConversationOpened(listener),
    [coordinator]
  );

  const deactivateEmbedded = useCallback(
    () => coordinator.deactivateEmbedded(),
    [coordinator]
  );

  const reportChatOpenedFromUnread = useCallback(
    (source: ChatUnreadSource) => coordinator.reportChatOpenedFromUnread(source),
    [coordinator]
  );

  const confirmExistingIdentity = useCallback(
    () => coordinator.useExistingIdentity(),
    [coordinator]
  );

  const continueAnonymously = useCallback(
    () => coordinator.continueAnonymously(),
    [coordinator]
  );

  const value = useMemo<ZendeskMessagingContextValue>(
    () => ({
      ...state,
      unread,
      applyCustomization,
      deactivateEmbedded,
      reopenEmbedded,
      reportChatOpenedFromUnread,
      confirmExistingIdentity,
      continueAnonymously,
      recordSupportEntryPoint,
      renderEmbedded,
      subscribeToClose,
      subscribeToConversationOpened,
      updateSupportRoute,
      retry: initialize
    }),
    [
      applyCustomization,
      continueAnonymously,
      deactivateEmbedded,
      initialize,
      renderEmbedded,
      recordSupportEntryPoint,
      reopenEmbedded,
      reportChatOpenedFromUnread,
      confirmExistingIdentity,
      state,
      subscribeToClose,
      subscribeToConversationOpened,
      updateSupportRoute,
      unread
    ]
  );

  return (
    <ZendeskMessagingContext.Provider value={value}>
      {children}
    </ZendeskMessagingContext.Provider>
  );
};

export const useZendeskMessaging = (): ZendeskMessagingContextValue => {
  const context = useContext(ZendeskMessagingContext);
  if (!context) {
    throw new Error('useZendeskMessaging must be used inside ZendeskMessagingProvider');
  }
  return context;
};
